import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/Login/LoginPage';
import { DashboardPage } from './pages/Dashboard/DashboardPage';
import { ClientsPage } from './pages/Clients/ClientsPage';
import { LicensesPage } from './pages/Licenses/LicensesPage';
import LicenseTypesPage from './pages/LicenseTypes/LicenseTypesPage';
import { SoftwarePage } from './pages/Software/SoftwarePage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { ForgotPasswordPage } from './pages/ForgotPassword/ForgotPasswordPage';
import { useAuthStore } from './store/authStore';
import { ROUTES } from './constants';
import { marketingRoutes } from './routes/marketingRoutes';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  
  return <>{children}</>;
};

// Admin Redirect Component
const AdminRedirect = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [isChecking, setIsChecking] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);
  
  React.useEffect(() => {
    const checkAdminAccess = () => {
      // Verificar si el usuario está autenticado
      if (!isAuthenticated) {
        setHasAccess(false);
        setIsChecking(false);
        return;
      }
      
      // Verificar si el usuario tiene permisos de staff o superuser
      if (user?.is_staff || user?.is_superuser) {
        setHasAccess(true);
        setIsChecking(false);
        
        // Redirigir al admin
        const adminUrl = import.meta.env.PROD 
          ? 'https://dicomity.com.ar/admin/' 
          : 'http://localhost:9000/admin/';
        
        window.location.href = adminUrl;
      } else {
        setHasAccess(false);
        setIsChecking(false);
      }
    };
    
    checkAdminAccess();
  }, [isAuthenticated, user]);
  
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permisos de administración...</p>
        </div>
      </div>
    );
  }
  
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso denegado</h3>
          <p className="text-gray-600 mb-4">No tienes permisos para acceder al panel de administración.</p>
          <button 
            onClick={() => window.history.back()}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Redirigiendo al panel de administración...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            {/* Marketing Routes (Public) */}
            {marketingRoutes}

            {/* Auth Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              } 
            />
            
            {/* Admin route - redirect to Django admin */}
            <Route 
              path="/admin" 
              element={<AdminRedirect />} 
            />
            <Route 
              path="/admin/*" 
              element={<AdminRedirect />} 
            />
            
            {/* App Routes (Protected) */}
            <Route 
              path="/app" 
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="licenses" element={<LicensesPage />} />
              <Route path="license-types" element={<LicenseTypesPage />} />
              <Route path="software" element={<SoftwarePage />} />
              
              {/* Placeholder routes for future pages */}
              <Route path="reports" element={<div className="p-6"><h1 className="text-2xl font-bold">Reportes</h1><p>Página de reportes en desarrollo...</p></div>} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
      
      {/* React Query Devtools - only in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;

